import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import GlobalSearch from './GlobalSearch';
import axios from 'axios';

const STORAGE_KEY = 'notif_last_seen';

const Header = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [notifOpen, setNotifOpen] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const userMenuRef = useRef(null);
  const notifRef = useRef(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchRecentOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/recent-orders`);
      if (res.data.success) {
        const orders = res.data.orders;
        setRecentOrders(orders);

        const lastSeen = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        const newCount = orders.filter(o => new Date(o.createdAt).getTime() > lastSeen).length;
        setUnreadCount(newCount);
      }
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    fetchRecentOrders();
    const interval = setInterval(fetchRecentOrders, 60000);
    return () => clearInterval(interval);
  }, [currentUser, fetchRecentOrders]);

  if (!currentUser || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  const handleOpenNotif = () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen) {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      setUnreadCount(0);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    const sidebar = document.querySelector('.sidebar');
    const isOpen = sidebar && sidebar.classList.contains('sidebar--mobile-open');
    if (sidebar) sidebar.classList.toggle('sidebar--mobile-open');
    document.body.classList.toggle('sidebar-mobile-open', !isOpen);

  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency', currency: 'THB',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'เมื่อกี้';
    if (diff < 3600) return `${Math.floor(diff / 60)} นาทีที่แล้ว`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ชั่วโมงที่แล้ว`;
    return d.toLocaleDateString('th-TH');
  };

  const getStatusColor = (status) => {
    const map = { created: 'success', pending: 'warning', approved: 'primary', rejected: 'danger' };
    return map[status] || 'secondary';
  };

  return (
    <header className="header">
      <div className="header__container">
        <button className="header__mobile-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
          <i className="bi bi-list"></i>
        </button>

        <div className="header__breadcrumb"><span>Workspace</span><i className="bi bi-chevron-right" /><strong>{location.pathname === "/" ? "Overview" : location.pathname.startsWith("/order") ? "Sales orders" : location.pathname.startsWith("/rental") ? "Rentals" : location.pathname === "/profile" ? "Profile" : location.pathname === "/help" ? "Help center" : "Operations"}</strong></div>
        <div className="header__search">
          <GlobalSearch />
        </div>

        <div className="header__actions">
          {/* Notifications */}
          <div className="position-relative" ref={notifRef}>
            <button className="header__action-btn" title="Notifications" onClick={handleOpenNotif}>
              <i className="bi bi-bell"></i>
              {unreadCount > 0 && (
                <span className="header__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '110%', width: 'min(360px, calc(100vw - 32px))',
                background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                zIndex: 1050, overflow: 'hidden'
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="fw-semibold">Sales Orders ล่าสุด (24h)</span>
                  <span className="badge bg-primary">{recentOrders.length}</span>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {recentOrders.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-inbox" style={{ fontSize: '32px' }}></i>
                      <div className="mt-2 small">ไม่มี Sales Order ใหม่ใน 24 ชั่วโมง</div>
                    </div>
                  ) : (
                    recentOrders.map((order) => (
                      <Link
                        key={order.id}
                        to={`/order/${order.id}`}
                        onClick={() => setNotifOpen(false)}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div style={{
                          padding: '10px 16px', borderBottom: '1px solid #f8f8f8',
                          transition: 'background 0.15s'
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold text-primary" style={{ fontSize: '13px' }}>
                                🆕 {order.salesOrderNo}
                                {order.tranId && <span className="text-muted ms-1" style={{ fontSize: '11px' }}>({order.tranId})</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: '12px' }}>
                                {order.customerName || order.customerCode}
                              </div>
                              <div style={{ fontSize: '12px' }}>
                                <span className="text-success fw-semibold">{formatCurrency(order.grandTotal)}</span>
                                <span className="text-muted ms-2">{order.saleRepName || order.staffCode}</span>
                              </div>
                            </div>
                            <div className="text-end">
                              <span className={`badge bg-${getStatusColor(order.status)}`} style={{ fontSize: '10px' }}>
                                {order.status}
                              </span>
                              <div className="text-muted mt-1" style={{ fontSize: '11px' }}>{formatTime(order.createdAt)}</div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>

                <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                  <Link to="/" onClick={() => setNotifOpen(false)} className="text-primary small text-decoration-none">
                    ดูทั้งหมด →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="header__user-menu" ref={userMenuRef}>
            <button className="header__user-btn" onClick={() => setUserMenuOpen(!userMenuOpen)} aria-label="User menu">
              {currentUser?.photo_url ? (
                <img src={currentUser.photo_url} alt="" className="header__avatar" />
              ) : (
                <div className="header__avatar header__avatar--placeholder">
                  {currentUser?.first_name?.charAt(0) || 'U'}
                </div>
              )}
            </button>

            {userMenuOpen && (
              <div className="header__dropdown">
                <div className="header__dropdown-header">
                  <div className="header__user-name">{currentUser?.first_name} {currentUser?.last_name}</div>
                  <div className="header__user-email">{currentUser?.email || 'No email'}</div>
                </div>
                <button className="header__dropdown-item" onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}>
                  <i className="bi bi-person-circle"></i>
                  <span>โปรไฟล์</span>
                </button>
                <div className="header__dropdown-divider"></div>
                <button className="header__dropdown-item header__dropdown-item--danger" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right"></i>
                  <span>ออกจากระบบ</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
